import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  ConnectionPointDto,
  GridConnectionLevel,
  MeterFrameDto,
  MeterFrameState,
  MeterFrameService,
  MeterFrameUnitType,
} from '@root/app/api/nts';
import { ConnectionRight } from '@shared/interfaces/nts/connection-right';
import { InputGroupComponent } from '@root/app/shared/components/input/input-group/input-group.component';
import { InputDirective } from '@root/app/shared/components/input/input/input.directive';
import { LabelDirective } from '@root/app/shared/components/input/label/label.directive';
import { SelectDirective } from '@root/app/shared/components/input/select/select.directive';
import { FormApiService } from '@app/core/services/form-api.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { InputNumberDirective } from '@shared/components/input/number/input-number.directive';
import { KeyValue } from '@shared/interfaces/key-value/key-value';
import { enumToKeyValue } from '@app/core/utils/enum-to-key-value';
import { sortByTranslation } from '@app/core/utils/sort-by-translation';
import { HasUnsavedChanges } from '@app/core/guards/unsaved-changes.guard';
import { ConnectionRightFormComponent } from '@shared/components/connection-right-form/connection-right-form.component';
import { createConnectionRightForm } from '@shared/forms/connection-right.formgroup';
import { TabsComponent } from '@shared/components/tabs/tabs.component';
import { TabComponent } from '@shared/components/tabs/tab/tab.component';
import { ModalService } from '@app/core/services/modal.service';
import { ToastService } from '@app/core/services/toast.service';
import { forkJoin, Observable } from 'rxjs';
import { systemServiceTypeOptions, systemServicesCapacityValidators } from '@shared/forms/system-services.form';

@Component({
  selector: 'app-meterframe-openform',
  imports: [
    InputDirective,
    InputGroupComponent,
    LabelDirective,
    ReactiveFormsModule,
    SelectDirective,
    TranslatePipe,
    InputNumberDirective,
    ConnectionRightFormComponent,
    TabsComponent,
    TabComponent,
  ],
  templateUrl: './meterframe-openform.component.html',
  styleUrl: './meterframe-openform.component.css',
})
export class MeterframeOpenformComponent implements OnInit, HasUnsavedChanges {
  public hasUnsavedChanges(): boolean {
    return this.form.dirty || this.connectionRightForm.dirty;
  }

  public meterFrame?: MeterFrameDto;
  public availableMeterFrames?: MeterFrameDto[] = [];
  public meterFrames?: MeterFrameDto[] = [];
  public meterFramesList: KeyValue[] = [];
  public primaryMeterFrame?: MeterFrameDto

  public connectionPoint?: ConnectionPointDto;
  public initialConnectionRightForm = createConnectionRightForm();
  public connectionRight?: ConnectionRight;
  public connectionRightForm = createConnectionRightForm();
  public connectionRightExpired = false;

  public gridConnectionLevelList: KeyValue[] = sortByTranslation(enumToKeyValue(GridConnectionLevel, { omitKeys: ['NotSpecified'], prefixValue: 'meterFrame.networkPlacementsOptions.' }));

  public unitTypeList: KeyValue[] = sortByTranslation(enumToKeyValue(MeterFrameUnitType, { omitKeys: ['NotSpecified'], prefixValue: 'meterFrame.unitTypeOptions.' }));
  public systemServiceTypeOptions = systemServiceTypeOptions;

  public form: FormGroup = new FormGroup({
    id: new FormControl(null),
    meterFrameType: new FormControl(null, Validators.required),
    primaryMeterFrameId: new FormControl(null),
    connectionPointId: new FormControl(null),
    connectionPointIdentification: new FormControl(null),
    unitType: new FormControl(null),
    priority: new FormControl(1),
    identification: new FormControl(null, Validators.required),
    transformer: new FormControl(null),
    networkPlacement: new FormControl(GridConnectionLevel.C),
    description: new FormControl(null),
    capacity: new FormControl(null),
    systemServicesType: new FormControl(null),
    systemServicesCapacity: new FormControl<number | null>(null, systemServicesCapacityValidators),
    status: new FormControl(null),
    conversionFactorA14: new FormControl(1, Validators.required),
    conversionFactorA23: new FormControl(1, Validators.required),
    conversionFactorR12: new FormControl(1, Validators.required),
    conversionFactorR34: new FormControl(1, Validators.required),
    initialConnectionRight: this.initialConnectionRightForm,
  });

  constructor(
    private readonly meterFrameService: MeterFrameService,
    private readonly formApiService: FormApiService,
    private readonly translateService: TranslateService,
    private readonly modalService: ModalService,
    private readonly toastService: ToastService,
  ) {
  }

  public ngOnInit(): void {
    if (this.connectionPoint) {
      this.form.get('connectionPointId')?.setValue(this.connectionPoint.id);
      this.form.get('connectionPointIdentification')?.setValue(this.connectionPoint.identification);
    }

    this.form.get('meterFrameType')?.valueChanges.subscribe((type: string) => {
      this.updateValidators(type);

      if (!this.meterFrame && type) {
        const name = `meterFrame.types.${type.toLowerCase()}`;
        this.form.get('identification')?.setValue(
          this.translateService.instant(name)
        );
      }
    });

    this.form.get('unitType')?.valueChanges.subscribe((unit: MeterFrameUnitType | null) => {
      if (this.meterFrame) {
        return;
      }

      const defaults: Record<string, number> = {
        ChargingStation: 100,
        HeatPump: 200,
        BatterySystem: 220,
      };

      const value = unit ? defaults[String(unit)] : undefined;
      if (value !== undefined) {
        this.form.get('priority')?.setValue(value);
      }

      if (unit) {
        const unitTranslationKey = `meterFrame.unitTypeOptions.${String(unit).toLowerCase()}`;
        this.form.get('identification')?.setValue(this.translateService.instant(unitTranslationKey));
      }
    });

    if (this.availableMeterFrames) {
      for (const element of this.availableMeterFrames) {
        if (element.meterFrameType === 'Primary' || element.meterFrameType === 'NotSpecified') {
          if (this.meterFrames) {
            this.meterFrames.push(element);
          }
          if (this.meterFrame?.primaryMeterFrameId === element.id) {
            this.primaryMeterFrame = element;
          }
        }
      }

      this.meterFramesList = this.meterFrames?.map(el => ({ value: el.identification, key: el.id })) as KeyValue[];

      this.populateFormWithMeterFrame();
      this.loadConnectionRight();
    }
  }

  public saveChanges(): void {
    const meterFrameChanged = this.form.dirty;
    const connectionRightChanged = this.connectionPoint?.connectionPointOwnsConnectionRights === false
      && !this.connectionRightExpired && this.connectionRightForm.dirty && this.connectionRightForm.enabled;

    if ((meterFrameChanged && !this.form.valid) || (connectionRightChanged && !this.connectionRightForm.valid)) {
      if (meterFrameChanged) {
        this.form.markAllAsTouched();
      }
      if (connectionRightChanged) {
        this.connectionRightForm.markAllAsTouched();
      }
      return;
    }

    const requests: Observable<unknown>[] = [];
    if (meterFrameChanged) {
      requests.push(this.meterFrameService.meterFrameEditPost(this.form.value));
    }
    if (connectionRightChanged && this.meterFrame?.id) {
      const connectionRight = this.connectionRightForm.getRawValue();
      requests.push(this.meterFrameService.meterFrameMeterFrameIdConnectionRightPost(
        this.meterFrame.id,
        connectionRight,
      ));
    }

    if (requests.length === 0) {
      this.modalService.close();
      return;
    }

    forkJoin(requests).subscribe(() => {
      if (meterFrameChanged) {
        this.toastService.show('meterFrame.meterFrameEdited');
      }
      if (connectionRightChanged) {
        this.toastService.show('connectionRight.success');
      }
      this.modalService.close(true);
    });
  }

  public createMeterFrame(): void {
    this.convertToString('transformer');

    this.form.get('status')?.setValue(MeterFrameState.Active);

    this.formApiService.submit(
      this.form,
      (data) => this.meterFrameService.meterFrameNewPost(data),
      {
        successMessage: 'meterFrame.meterFrameCreated',
        closeModal: true,
      }
    );
  }

  public convertToString(key: string): void {
    if (this.form.get(key)?.value) {
      this.form.get(key)?.setValue(String(this.form.get(key)?.value));
    }
  }

  private updateValidators(type: string | null): void {
    this.form.get('priority')?.clearValidators();
    this.form.get('primaryMeterFrameId')?.clearValidators();
    this.form.get('unitType')?.clearValidators();
    this.form.get('capacity')?.clearValidators();
    this.form.get('networkPlacement')?.clearValidators();

    if (type === 'Primary' || type === 'Station') {
      this.form.get('priority')?.setValidators(Validators.required);
      this.form.get('networkPlacement')?.setValidators(Validators.required);
    } else if (type === 'Serial' || type === 'Parallel') {
      this.form.get('primaryMeterFrameId')?.setValidators(Validators.required);
      this.form.get('unitType')?.setValidators(Validators.required);
      this.form.get('capacity')?.setValidators(Validators.required);
    }

    this.form.get('priority')?.updateValueAndValidity();
    this.form.get('primaryMeterFrameId')?.updateValueAndValidity();
    this.form.get('unitType')?.updateValueAndValidity();
    this.form.get('capacity')?.updateValueAndValidity();
    this.form.get('networkPlacement')?.updateValueAndValidity();
  }

  private populateFormWithMeterFrame(): void {
    if (this.meterFrame) {
      this.form.patchValue({
        id: this.meterFrame.id,
        meterFrameType: this.meterFrame.meterFrameType,
        primaryMeterFrameId: this.meterFrame.primaryMeterFrameId,
        unitType: this.meterFrame.unitType,
        priority: this.meterFrame.priority,
        identification: this.meterFrame.identification,
        transformer: this.meterFrame.transformer,
        networkPlacement: this.meterFrame.networkPlacement,
        description: this.meterFrame.description,
        capacity: this.meterFrame.capacity,
        systemServicesType: this.meterFrame.systemServicesType,
        systemServicesCapacity: this.meterFrame.systemServicesCapacity,
        conversionFactorA14: this.meterFrame.conversionFactorA14,
        conversionFactorA23: this.meterFrame.conversionFactorA23,
        conversionFactorR12: this.meterFrame.conversionFactorR12,
        conversionFactorR34: this.meterFrame.conversionFactorR34,
      });

      this.updateValidators(this.meterFrame.meterFrameType!);
    }
  }

  private loadConnectionRight(): void {
    if (!this.meterFrame?.id || this.connectionPoint?.connectionPointOwnsConnectionRights !== false) {
      return;
    }

    this.connectionRightExpired = false;
    this.connectionRightForm.disable({ emitEvent: false });
    this.meterFrameService.meterFrameMeterFrameIdConnectionRightGet(this.meterFrame.id)
      .subscribe(right => {
        this.connectionRight = right ?? undefined;
        this.connectionRightForm = createConnectionRightForm(this.connectionRight);
        this.connectionRightForm.markAsPristine();
        this.connectionRightExpired = right?.isExpired === true || right?.isAutomaticallyExpired === true;
        if (this.connectionRightExpired) {
          this.connectionRightForm.disable({ emitEvent: false });
        }
      });
  }
}
